import { useState, useEffect, useCallback } from 'react';

export interface UseNumberInputOptions {
  /** Minimum allowed value (default: -Infinity) */
  min?: number;
  /** Maximum allowed value (default: Infinity) */
  max?: number;
  /** Default value when input is empty on blur (default: 0) */
  defaultValue?: number;
  /** Whether to allow decimal values (default: true) */
  allowDecimals?: boolean;
  /** Whether to allow negative values (default: false) */
  allowNegative?: boolean;
}

export interface UseNumberInputReturn {
  /** The current string value for the input */
  value: string;
  /** Handler for input change events */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Handler for input blur events - validates and clamps the value */
  onBlur: () => void;
}

/**
 * Hook for number inputs with better typing UX.
 *
 * This hook allows users to fully clear the input while typing (including
 * deleting the last digit), then validates and clamps the value on blur.
 * If the input is empty on blur, it defaults to the specified defaultValue (0 by default).
 *
 * @example
 * ```tsx
 * const [score, setScore] = useState(0);
 * const input = useNumberInput(score, setScore, { min: 0 });
 *
 * <TextField
 *   type="number"
 *   value={input.value}
 *   onChange={input.onChange}
 *   onBlur={input.onBlur}
 * />
 * ```
 */
export function useNumberInput(
  value: number,
  onChange: (value: number) => void,
  options: UseNumberInputOptions = {}
): UseNumberInputReturn {
  const {
    min = -Infinity,
    max = Infinity,
    defaultValue = 0,
    allowDecimals = true,
    allowNegative = false,
  } = options;

  const [inputValue, setInputValue] = useState(value.toString());

  // Sync external value changes
  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Allow empty string for clearing
    if (newValue === '') {
      setInputValue('');
      return;
    }

    // Build regex based on options
    let pattern: RegExp;
    if (allowDecimals && allowNegative) {
      pattern = /^-?\d*\.?\d*$/;
    } else if (allowDecimals) {
      pattern = /^\d*\.?\d*$/;
    } else if (allowNegative) {
      pattern = /^-?\d*$/;
    } else {
      pattern = /^\d*$/;
    }

    // Allow negative sign by itself while typing
    if (allowNegative && newValue === '-') {
      setInputValue('-');
      return;
    }

    // Validate pattern
    if (pattern.test(newValue)) {
      setInputValue(newValue);
    }
  }, [allowDecimals, allowNegative]);

  const handleBlur = useCallback(() => {
    const parsed = parseFloat(inputValue);

    if (isNaN(parsed) || inputValue === '' || inputValue === '-') {
      // Set to default value if invalid or empty
      setInputValue(defaultValue.toString());
      if (value !== defaultValue) {
        onChange(defaultValue);
      }
    } else {
      // Clamp and update
      const clamped = Math.max(min, Math.min(max, parsed));
      setInputValue(clamped.toString());
      if (clamped !== value) {
        onChange(clamped);
      }
    }
  }, [inputValue, value, onChange, min, max, defaultValue]);

  return {
    value: inputValue,
    onChange: handleChange,
    onBlur: handleBlur,
  };
}
